import { Injectable, inject } from '@angular/core';
import {
  Action,
  Selector,
  State,
  type StateContext,
  StateToken,
} from '@ngxs/store';
import { uniqBy } from 'lodash-es';
import { tap } from 'rxjs/operators';

import type { CommentDto } from '@plugins/faq-lib/types';

import { CommentStateModel } from '../../../types';
import { DEFAULT_LIKE_DTO } from '../../constants';
import { CommentService } from '../../services/comment.service';

import {
  CommentsLoaded,
  CreateComment,
  DeleteComment,
  LikeComment,
  MoreCommentsLoaded,
  SubscribedToAnswer,
  UpdateComment,
} from './comment.actions';

const COMMENT_STATE_TOKEN = new StateToken<CommentStateModel>('comment');

@State<CommentStateModel>({
  name: COMMENT_STATE_TOKEN,
  defaults: {
    comments: [],
  },
})
@Injectable()
export class CommentState {
  private readonly commentService = inject(CommentService);

  @Selector()
  static commentsByAnswers(state: CommentStateModel) {
    return state.comments.reduce(
      (acc, curr) => {
        if (acc[curr.answerId]) {
          acc[curr.answerId].push(curr);
        } else {
          acc[curr.answerId] = [curr];
        }
        return acc;
      },
      {} as Record<string, CommentDto[]>,
    );
  }

  @Action(CommentsLoaded)
  commentsLoaded(
    { patchState }: StateContext<CommentStateModel>,
    { loadedComments }: CommentsLoaded,
  ) {
    patchState({
      comments: loadedComments,
    });
  }

  @Action(MoreCommentsLoaded)
  moreCommentsLoaded(
    { getState, patchState }: StateContext<CommentStateModel>,
    { loadedComments }: CommentsLoaded,
  ) {
    const { comments } = getState();
    patchState({
      comments: uniqBy([...comments, ...loadedComments], 'id'),
    });
  }

  @Action(CreateComment)
  createComment(
    { getState, patchState, dispatch }: StateContext<CommentStateModel>,
    { comment }: CreateComment,
  ) {
    return this.commentService.create(comment).pipe(
      tap((res) => {
        const { comments } = getState();
        patchState({
          comments: [...comments, { ...res.comment, ...DEFAULT_LIKE_DTO }],
        });
        return dispatch(new SubscribedToAnswer(res.answerSubscription));
      }),
    );
  }

  @Action(UpdateComment)
  updateComment(
    { getState, patchState }: StateContext<CommentStateModel>,
    { comment }: UpdateComment,
  ) {
    return this.commentService.update(comment).pipe(
      tap((updatedComment) => {
        const { comments } = getState();
        patchState({
          comments: comments.map((c) =>
            c.id === updatedComment.id ? { ...c, ...updatedComment } : c,
          ),
        });
      }),
    );
  }

  @Action(DeleteComment)
  deleteComment(
    { getState, patchState }: StateContext<CommentStateModel>,
    { commentId }: DeleteComment,
  ) {
    return this.commentService.delete(commentId).pipe(
      tap(() => {
        const { comments } = getState();
        patchState({
          comments: comments.map((c) =>
            c.id === commentId ? { ...c, deleted: true } : c,
          ),
        });
      }),
    );
  }

  @Action(LikeComment)
  likeComment(
    { getState, patchState }: StateContext<CommentStateModel>,
    { commentId }: DeleteComment,
  ) {
    return this.commentService.like(commentId).pipe(
      tap((vote) => {
        const { comments } = getState();
        patchState({
          comments: comments.map((c) =>
            c.id === commentId
              ? {
                  ...c,
                  liked: vote.points,
                  totalLikes: (c.totalLikes || 0) + (vote.points ? 1 : -1),
                }
              : c,
          ),
        });
      }),
    );
  }
}
